import { strengthExerciseById } from "./workoutCatalog";

const KG_PER_LB = 0.45359237;
const CM_PER_IN = 2.54;
const CM_PER_FT = 30.48;
const GRAVITY_M_S2 = 9.80665;
const JOULES_PER_KCAL = 4184;
const MUSCLE_EFFICIENCY = 0.25;
const CARDIO_EFFICIENCY = 0.24;
const STRENGTH_REP_SECONDS = 2.5;
const STRENGTH_SETUP_SECONDS = 60;
const STRENGTH_REST_SECONDS = 120;
const STRENGTH_TRANSITION_SECONDS = 45;
const STRENGTH_MET_DEFAULT = 7.2;
const STEPS_KCAL_PER_KG = 0.0005;
const STEP_BASELINE_BY_ACTIVITY = {
  desk: 3000,
  standing: 6000,
  manual: 9000,
};
const STRENGTH_EFFORT_MET = {
  light: 5.5,
  moderate: 7.5,
  hard: 9.5,
};

export const parseNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const clamp = (value, min, max) =>
  Math.min(max, Math.max(min, value));

export const convertWeightToKg = (value, unit) => {
  const weight = parseNumber(value);
  if (!weight) return null;
  return unit === "lb" ? weight * KG_PER_LB : weight;
};

export const convertKgToUnit = (value, unit) => {
  if (!Number.isFinite(value)) return null;
  return unit === "lb" ? value / KG_PER_LB : value;
};

export const calculateLeanMassKg = ({ weightKg, bodyFatPct }) => {
  const fatPct = parseNumber(bodyFatPct);
  if (!Number.isFinite(weightKg) || weightKg <= 0) return null;
  if (!Number.isFinite(fatPct) || fatPct <= 0 || fatPct >= 80) {
    return null;
  }
  const leanMass = weightKg * (1 - fatPct / 100);
  return leanMass > 0 ? leanMass : null;
};

export const convertHeightToCm = ({
  unit,
  heightCm,
  heightFt,
  heightIn,
}) => {
  if (unit === "cm") {
    const cmValue = parseNumber(heightCm);
    return cmValue || null;
  }
  const feet = parseNumber(heightFt) || 0;
  const inches = parseNumber(heightIn) || 0;
  const total = feet * CM_PER_FT + inches * CM_PER_IN;
  return total > 0 ? total : null;
};

export const convertCmToFeetInches = (cm) => {
  if (!Number.isFinite(cm) || cm <= 0) {
    return { feet: "", inches: "" };
  }
  const totalInches = cm / CM_PER_IN;
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches - feet * 12);
  return { feet, inches };
};

export const getBaseActivityMultiplier = (jobActivity) => {
  const map = {
    desk: 1.2,
    standing: 1.35,
    manual: 1.55,
  };
  if (!jobActivity) {
    return { baseActivity: 1.2, assumed: true };
  }
  return { baseActivity: map[jobActivity] || 1.2, assumed: false };
};

export const getCalculatorNetActivity = ({
  trainingFrequency,
  jobActivity,
}) => {
  const frequency = parseNumber(trainingFrequency);
  const clampedFrequency = Number.isFinite(frequency)
    ? clamp(frequency, 0, 7)
    : null;
  const levelFromDays = (days) => {
    if (days <= 1) return { label: "Sedentary", multiplier: 1.2 };
    if (days <= 3)
      return { label: "Lightly active", multiplier: 1.375 };
    if (days <= 5)
      return { label: "Moderately active", multiplier: 1.55 };
    return { label: "Very active", multiplier: 1.725 };
  };
  const jobMinimums = {
    desk: { label: "Sedentary", multiplier: 1.2 },
    standing: { label: "Lightly active", multiplier: 1.375 },
    manual: { label: "Very active", multiplier: 1.725 },
  };
  const base = Number.isFinite(clampedFrequency)
    ? levelFromDays(clampedFrequency)
    : jobActivity
      ? jobMinimums[jobActivity]
      : levelFromDays(0);
  const jobMinimum = jobActivity ? jobMinimums[jobActivity] : null;
  const multiplier =
    jobMinimum && jobMinimum.multiplier > base.multiplier
      ? jobMinimum.multiplier
      : base.multiplier;
  const label = levelFromDays(
    multiplier >= 1.725
      ? 6
      : multiplier >= 1.55
        ? 4
        : multiplier >= 1.375
          ? 2
          : 0
  ).label;
  return { multiplier, label };
};
export const getTrainingModifiers = ({
  trainingFrequency,
  trainingIntensity,
  trainingModality,
  sleepQuality,
  dietHistory,
  trainingLoadAdd = 0,
  stepsLoadAdd = 0,
  useWorkoutCalories = false,
}) => {
  const intensityMap = {
    light: 0,
    moderate: 0.03,
    hard: 0.06,
  };
  const modalityMap = {
    strength: 0.02,
    endurance: 0.03,
    mixed: 0.025,
  };
  const sleepMap = {
    poor: -0.03,
    ok: 0,
    good: 0.01,
  };
  const dietMap = {
    cutting: -0.01,
    maintenance: 0,
    bulking: 0.01,
  };

  const frequency = parseNumber(trainingFrequency);
  const freqAdd =
    !useWorkoutCalories &&
    Number.isFinite(frequency) &&
    frequency >= 0
      ? frequency * 0.02
      : 0;

  return {
    freqAdd,
    intensityAdd: useWorkoutCalories
      ? 0
      : intensityMap[trainingIntensity] || 0,
    modalityAdd: useWorkoutCalories
      ? 0
      : modalityMap[trainingModality] || 0,
    sleepAdd: sleepMap[sleepQuality] || 0,
    dietHistoryAdd: dietMap[dietHistory] || 0,
    trainingLoadAdd,
    stepsLoadAdd,
  };
};


export const calculateBmr = ({
  sex,
  age,
  heightCm,
  weightKg,
  bodyFatPct,
}) => {
  if (!sex || !age || !heightCm || !weightKg) return null;
  const leanMassKg = calculateLeanMassKg({
    weightKg,
    bodyFatPct,
  });
  if (Number.isFinite(leanMassKg)) {
    return 370 + 21.6 * leanMassKg;
  }
  const base = 10 * weightKg + 6.25 * heightCm - 5 * age;
  return sex === "male" ? base + 5 : base - 161;
};

export const calculateTdee = ({
  bmr,
  baseActivity,
  modifiers,
  maxMultiplier = 2.4,
}) => {
  if (!bmr) return null;
  const totalMultiplier =
    baseActivity +
    modifiers.freqAdd +
    modifiers.intensityAdd +
    modifiers.modalityAdd +
    modifiers.sleepAdd +
    modifiers.dietHistoryAdd +
    (modifiers.trainingLoadAdd || 0) +
    (modifiers.stepsLoadAdd || 0);
  const clamped = clamp(totalMultiplier, 1.1, maxMultiplier);
  const wasClamped = clamped !== totalMultiplier;
  return {
    totalMultiplier: clamped,
    rawMultiplier: totalMultiplier,
    wasClamped,
    tdee: bmr * clamped,
  };
};

export const calculateStepsAdjustment = ({
  steps,
  weightKg,
  jobActivity,
}) => {
  const baselineSteps =
    STEP_BASELINE_BY_ACTIVITY[jobActivity] || 3000;
  const stepCount = parseNumber(steps);
  if (!Number.isFinite(stepCount) || stepCount <= 0) {
    return { stepsCaloriesPerDay: 0, baselineSteps };
  }
  if (!Number.isFinite(weightKg) || weightKg <= 0) {
    return { stepsCaloriesPerDay: 0, baselineSteps };
  }
  const effectiveSteps = Math.max(0, stepCount - baselineSteps);
  return {
    stepsCaloriesPerDay:
      effectiveSteps * weightKg * STEPS_KCAL_PER_KG,
    baselineSteps,
  };
};

const strengthRowComplete = (exercise) => {
  if (!exercise.exerciseId) return false;
  const sets = parseNumber(exercise.sets);
  const reps = parseNumber(exercise.reps);
  const weight = parseNumber(exercise.weight);
  const exerciseMeta =
    strengthExerciseById[exercise.exerciseId];
  const requiresExternalWeight = !exerciseMeta?.usesBodyweight;
  return (
    Number.isFinite(sets) &&
    sets > 0 &&
    Number.isFinite(reps) &&
    reps > 0 &&
    (!requiresExternalWeight ||
      (Number.isFinite(weight) && weight > 0))
  );
};

const cardioHasIntensity = (entry) => {
  const keysByType = {
    treadmill: ["speed", "incline"],
    stairmaster: ["level"],
    elliptical: ["resistance", "incline"],
    bike: ["resistance", "watts"],
    rowing: ["pace", "resistance"],
    other: ["intensityNote"],
  };
  const keys = keysByType[entry.type] || [];
  return keys.some((key) => {
    const value = entry[key];
    if (typeof value === "string") {
      return value.trim().length > 0;
    }
    const parsed = parseNumber(value);
    return Number.isFinite(parsed) && parsed > 0;
  });
};

const getStrengthTonnageKg = (
  strengthWorkouts = [],
  strengthUnit,
  bodyWeightKg
) =>
  strengthWorkouts.reduce((total, workout) => {
    const exercises = workout.exercises || [];
    const hasExercises = exercises.some(
      (exercise) => !!exercise.exerciseId
    );
    const sessionsPerWeek = parseNumber(workout.sessionsPerWeek);
    const sessionCount = Number.isFinite(sessionsPerWeek)
      ? clamp(sessionsPerWeek, 0, 7)
      : hasExercises
        ? 1
        : 0;
    if (!hasExercises || sessionCount <= 0) return total;
    const tonnagePerSession = exercises.reduce(
      (exerciseTotal, exercise) => {
        const sets = parseNumber(exercise.sets);
        const reps = parseNumber(exercise.reps);
        const weight = parseNumber(exercise.weight);
        const exerciseMeta =
          strengthExerciseById[exercise.exerciseId];
        const usesBodyweight = exerciseMeta?.usesBodyweight;
        if (
          !exercise.exerciseId ||
          !Number.isFinite(sets) ||
          sets <= 0 ||
          !Number.isFinite(reps) ||
          reps <= 0
        ) {
          return exerciseTotal;
        }
        const addedWeightKg =
          Number.isFinite(weight) && weight > 0
            ? strengthUnit === "lb"
              ? weight * KG_PER_LB
              : weight
            : 0;
        const baseWeightKg =
          usesBodyweight && Number.isFinite(bodyWeightKg)
            ? bodyWeightKg
            : 0;
        const effectiveWeightKg = usesBodyweight
          ? baseWeightKg + addedWeightKg
          : addedWeightKg;
        if (
          !Number.isFinite(effectiveWeightKg) ||
          effectiveWeightKg <= 0
        ) {
          return exerciseTotal;
        }
        return exerciseTotal + sets * reps * effectiveWeightKg;
      },
      0
    );
    return total + tonnagePerSession * sessionCount;
  }, 0);

const getCardioWeeklyMinutes = (cardioSessions) =>
  cardioSessions.reduce((total, entry) => {
    const sessions = parseNumber(entry.sessionsPerWeek);
    const duration = parseNumber(entry.duration);
    if (
      !Number.isFinite(sessions) ||
      sessions <= 0 ||
      !Number.isFinite(duration) ||
      duration <= 0
    ) {
      return total;
    }
    return total + sessions * duration;
  }, 0);

const estimateStrengthExerciseMinutes = ({ sets, reps }) => {
  if (!Number.isFinite(sets) || sets <= 0) return 0;
  if (!Number.isFinite(reps) || reps <= 0) return 0;
  const restSeconds = STRENGTH_REST_SECONDS;
  const workSeconds = sets * reps * STRENGTH_REP_SECONDS;
  const restTotal = Math.max(sets - 1, 0) * restSeconds;
  return (workSeconds + restTotal) / 60;
};

const estimateStrengthWorkoutMinutes = (exercises) => {
  const validExercises = exercises.filter((exercise) => {
    const sets = parseNumber(exercise.sets);
    const reps = parseNumber(exercise.reps);
    return (
      exercise.exerciseId &&
      Number.isFinite(sets) &&
      sets > 0 &&
      Number.isFinite(reps) &&
      reps > 0
    );
  });
  const exerciseMinutes = validExercises.reduce(
    (total, exercise) =>
      total +
      estimateStrengthExerciseMinutes({
        sets: parseNumber(exercise.sets),
        reps: parseNumber(exercise.reps),
      }),
    0
  );
  if (exerciseMinutes <= 0) return 0;
  const transitionSeconds =
    Math.max(validExercises.length - 1, 0) *
    STRENGTH_TRANSITION_SECONDS;
  const totalSeconds =
    exerciseMinutes * 60 +
    STRENGTH_SETUP_SECONDS +
    transitionSeconds;
  return totalSeconds / 60;
};

const getStrengthMetFromEffort = (effort) =>
  STRENGTH_EFFORT_MET[effort] || STRENGTH_MET_DEFAULT;

const calculateCaloriesFromMet = (met, weightKg, duration) => {
  if (!Number.isFinite(met) || met <= 0) return 0;
  if (!Number.isFinite(weightKg) || weightKg <= 0) return 0;
  if (!Number.isFinite(duration) || duration <= 0) return 0;
  return (met * weightKg * duration) / 60;
};

const calculateTreadmillCalories = ({
  speedKmh,
  inclinePercent,
  duration,
  weightKg,
}) => {
  if (!Number.isFinite(speedKmh) || speedKmh <= 0) return 0;
  if (!Number.isFinite(duration) || duration <= 0) return 0;
  if (!Number.isFinite(weightKg) || weightKg <= 0) return 0;
  const speedMMin = (speedKmh * 1000) / 60;
  const grade = Math.max(0, (inclinePercent || 0) / 100);
  const isRunning = speedMMin >= 100;
  const horizontal = isRunning ? 0.2 : 0.1;
  const vertical = isRunning ? 0.9 : 1.8;
  const vo2 =
    speedMMin * horizontal + speedMMin * grade * vertical + 3.5;
  const kcalPerMin = (vo2 * weightKg * 5) / 1000;
  return kcalPerMin * duration;
};

const parsePaceSeconds = (pace) => {
  if (!pace) return null;
  const raw = String(pace).trim();
  if (!raw) return null;
  if (raw.includes(":")) {
    const parts = raw.split(":");
    const minutes = parseNumber(parts[0]);
    const seconds = parseNumber(parts[1]);
    if (!Number.isFinite(minutes) || !Number.isFinite(seconds)) {
      return null;
    }
    return minutes * 60 + seconds;
  }
  const numeric = parseNumber(raw);
  if (!Number.isFinite(numeric)) return null;
  return numeric < 30 ? numeric * 60 : numeric;
};

const calculateRowingCalories = ({
  pace,
  resistance,
  duration,
  weightKg,
}) => {
  const paceSeconds = parsePaceSeconds(pace);
  if (paceSeconds && paceSeconds > 0) {
    const clampedPace = clamp(paceSeconds, 90, 600);
    const watts =
      2.8 / Math.pow(clampedPace / 500, 3);
    const kcalPerMin =
      (watts * 60) / (CARDIO_EFFICIENCY * JOULES_PER_KCAL);
    if (!Number.isFinite(duration) || duration <= 0) return 0;
    return kcalPerMin * duration;
  }
  const intensity = parseNumber(resistance) || 0;
  const met = clamp(5 + intensity * 0.3, 5, 12);
  return calculateCaloriesFromMet(met, weightKg, duration);
};

const calculateBikeCalories = ({
  watts,
  resistance,
  duration,
  weightKg,
}) => {
  const wattsValue = parseNumber(watts);
  if (Number.isFinite(wattsValue) && wattsValue > 0) {
    const kcalPerMin =
      (wattsValue * 60) /
      (CARDIO_EFFICIENCY * JOULES_PER_KCAL);
    if (!Number.isFinite(duration) || duration <= 0) return 0;
    return kcalPerMin * duration;
  }
  const intensity = parseNumber(resistance) || 0;
  const met = clamp(4 + intensity * 0.4, 4, 12);
  return calculateCaloriesFromMet(met, weightKg, duration);
};

const calculateEllipticalCalories = ({
  resistance,
  incline,
  duration,
  weightKg,
}) => {
  const intensity =
    (parseNumber(resistance) || 0) * 0.4 +
    (parseNumber(incline) || 0) * 0.2;
  const met = clamp(5 + intensity, 4, 12);
  return calculateCaloriesFromMet(met, weightKg, duration);
};

const calculateStairmasterCalories = ({
  level,
  duration,
  weightKg,
}) => {
  const intensity = parseNumber(level) || 0;
  const met = clamp(4 + intensity * 0.5, 4, 12);
  return calculateCaloriesFromMet(met, weightKg, duration);
};

const calculateGenericCardioCalories = ({ duration, weightKg }) =>
  calculateCaloriesFromMet(5, weightKg, duration);

const calculateCardioSessionCalories = (entry, weightKg) => {
  const duration = parseNumber(entry.duration);
  if (!Number.isFinite(duration) || duration <= 0) return 0;
  switch (entry.type) {
    case "treadmill":
      return calculateTreadmillCalories({
        speedKmh: parseNumber(entry.speed),
        inclinePercent: parseNumber(entry.incline),
        duration,
        weightKg,
      });
    case "stairmaster":
      return calculateStairmasterCalories({
        level: parseNumber(entry.level),
        duration,
        weightKg,
      });
    case "elliptical":
      return calculateEllipticalCalories({
        resistance: parseNumber(entry.resistance),
        incline: parseNumber(entry.incline),
        duration,
        weightKg,
      });
    case "bike":
      return calculateBikeCalories({
        watts: parseNumber(entry.watts),
        resistance: parseNumber(entry.resistance),
        duration,
        weightKg,
      });
    case "rowing":
      return calculateRowingCalories({
        pace: entry.pace,
        resistance: parseNumber(entry.resistance),
        duration,
        weightKg,
      });
    default:
      return calculateGenericCardioCalories({ duration, weightKg });
  }
};

const calculateStrengthCaloriesPerWeek = ({
  strengthWorkouts = [],
  strengthUnit,
  bodyWeightKg,
}) =>
  strengthWorkouts.reduce((total, workout) => {
    const exercises = workout.exercises || [];
    const hasExercises = exercises.some(
      (exercise) => !!exercise.exerciseId
    );
    const sessionsPerWeek = parseNumber(workout.sessionsPerWeek);
    const sessionCount = Number.isFinite(sessionsPerWeek)
      ? clamp(sessionsPerWeek, 0, 7)
      : hasExercises
        ? 1
        : 0;
    if (!hasExercises || sessionCount <= 0) {
      return total;
    }
    const providedMinutes = parseNumber(workout.durationMinutes);
    const sessionMinutes =
      Number.isFinite(providedMinutes) && providedMinutes > 0
        ? providedMinutes
        : estimateStrengthWorkoutMinutes(exercises);
    if (!Number.isFinite(sessionMinutes) || sessionMinutes <= 0) {
      return total;
    }
    const mechanicalKcalPerSession = exercises.reduce(
      (exerciseTotal, exercise) => {
        const sets = parseNumber(exercise.sets);
        const reps = parseNumber(exercise.reps);
        const weight = parseNumber(exercise.weight);
        const exerciseMeta =
          strengthExerciseById[exercise.exerciseId];
        const usesBodyweight = exerciseMeta?.usesBodyweight;
        if (
          !exercise.exerciseId ||
          !Number.isFinite(sets) ||
          sets <= 0 ||
          !Number.isFinite(reps) ||
          reps <= 0
        ) {
          return exerciseTotal;
        }
        const displacement = exerciseMeta?.displacementM || 0;
        if (!displacement) return exerciseTotal;
        const addedWeightKg =
          Number.isFinite(weight) && weight > 0
            ? strengthUnit === "lb"
              ? weight * KG_PER_LB
              : weight
            : 0;
        const baseWeightKg =
          usesBodyweight && Number.isFinite(bodyWeightKg)
            ? bodyWeightKg
            : 0;
        const effectiveWeightKg = usesBodyweight
          ? baseWeightKg + addedWeightKg
          : addedWeightKg;
        if (
          !Number.isFinite(effectiveWeightKg) ||
          effectiveWeightKg <= 0
        ) {
          return exerciseTotal;
        }
        const workJoulesPerSession =
          effectiveWeightKg *
          GRAVITY_M_S2 *
          displacement *
          reps *
          sets;
        const mechanicalKcal =
          workJoulesPerSession /
          (MUSCLE_EFFICIENCY * JOULES_PER_KCAL);
        return exerciseTotal + mechanicalKcal;
      },
      0
    );
    const met = getStrengthMetFromEffort(workout.effort);
    const timeKcal = calculateCaloriesFromMet(
      met,
      bodyWeightKg,
      sessionMinutes
    );
    const sessionKcal =
      timeKcal + mechanicalKcalPerSession * 0.5;
    return total + sessionKcal * sessionCount;
  }, 0);

const calculateCardioCaloriesPerWeek = ({
  cardioSessions,
  weightKg,
}) =>
  cardioSessions.reduce((total, session) => {
    const sessions = parseNumber(session.sessionsPerWeek);
    if (!Number.isFinite(sessions) || sessions <= 0) {
      return total;
    }
    const perSession = calculateCardioSessionCalories(
      session,
      weightKg
    );
    return total + perSession * sessions;
  }, 0);

export const deriveTrainingProfile = ({
  strengthWorkouts = [],
  cardioSessions = [],
  strengthUnit = "kg",
  weightKg,
}) => {
  const strengthSessions = strengthWorkouts.reduce(
    (total, workout) => {
      const sessionsPerWeek = parseNumber(workout.sessionsPerWeek);
      const hasExercises = (workout.exercises || []).some(
        (exercise) => !!exercise.exerciseId
      );
      const sessionCount = Number.isFinite(sessionsPerWeek)
        ? clamp(sessionsPerWeek, 0, 7)
        : hasExercises
          ? 1
          : 0;
      if (!hasExercises || sessionCount <= 0) {
        return total;
      }
      return total + sessionCount;
    },
    0
  );
  const cardioSessionsCounts = cardioSessions
    .map((session) => parseNumber(session.sessionsPerWeek))
    .filter((value) => Number.isFinite(value) && value > 0);

  const cardioSum = cardioSessionsCounts.reduce(
    (total, value) => total + value,
    0
  );
  const workoutSessionsPerWeek = strengthSessions + cardioSum;
  const totalFrequency = clamp(workoutSessionsPerWeek, 0, 7);

  const strengthTonnageKg = getStrengthTonnageKg(
    strengthWorkouts,
    strengthUnit,
    weightKg
  );
  const cardioMinutes = getCardioWeeklyMinutes(cardioSessions);
  const strengthCaloriesPerWeek = calculateStrengthCaloriesPerWeek({
    strengthWorkouts,
    strengthUnit,
    bodyWeightKg: weightKg,
  });
  const cardioCaloriesPerWeek = calculateCardioCaloriesPerWeek({
    cardioSessions,
    weightKg,
  });
  const workoutCaloriesPerWeek =
    strengthCaloriesPerWeek + cardioCaloriesPerWeek;
  const workoutCaloriesPerDay = workoutCaloriesPerWeek / 7;

  const strengthExercises = strengthWorkouts.flatMap(
    (workout) => workout.exercises || []
  );
  const strengthHasLoad = strengthExercises.some(
    strengthRowComplete
  );
  const maxHeartRate = cardioSessions
    .map((entry) => parseNumber(entry.heartRate))
    .filter((value) => Number.isFinite(value))
    .reduce((max, value) => Math.max(max, value), 0);
  const hasCardioIntensity = cardioSessions.some((entry) =>
    cardioHasIntensity(entry)
  );

  let trainingIntensity = "";
  if (totalFrequency > 0) {
    const heavyLoad =
      workoutCaloriesPerWeek >= 2000 ||
      strengthTonnageKg >= 90000 ||
      cardioMinutes >= 240;
    const moderateLoad =
      workoutCaloriesPerWeek >= 900 ||
      strengthTonnageKg >= 30000 ||
      cardioMinutes >= 120;
    if (
      maxHeartRate >= 160 ||
      heavyLoad
    ) {
      trainingIntensity = "hard";
    } else if (
      maxHeartRate >= 135 ||
      hasCardioIntensity ||
      strengthHasLoad ||
      moderateLoad
    ) {
      trainingIntensity = "moderate";
    } else {
      trainingIntensity = "light";
    }
  }

  let trainingModality = "";
  if (strengthSessions > 0 && cardioSum > 0) {
    trainingModality = "mixed";
  } else if (strengthSessions > 0) {
    trainingModality = "strength";
  } else if (cardioSum > 0) {
    trainingModality = "endurance";
  }

  return {
    trainingFrequency: String(Math.round(totalFrequency)),
    trainingIntensity,
    trainingModality,
    strengthSessions,
    cardioSessions: cardioSum,
    workoutSessionsPerWeek,
    strengthTonnageKg,
    cardioMinutes,
    strengthCaloriesPerWeek,
    cardioCaloriesPerWeek,
    workoutCaloriesPerWeek,
    workoutCaloriesPerDay,
  };
};

export const calculateConfidence = ({
  basicsValid,
  lifestyle,
  strengthWorkouts = [],
  cardioSessions,
  bodyFatPct,
  trainingSource = "manual",
}) => {
  let score = basicsValid ? 35 : 0;

  const stepsValue = parseNumber(lifestyle.dailySteps);
  const hasSteps =
    Number.isFinite(stepsValue) && stepsValue > 0;
  const bodyFatValue = parseNumber(bodyFatPct);
  const hasBodyFat =
    Number.isFinite(bodyFatValue) && bodyFatValue > 0;

  const smartInputs = [
    lifestyle.jobActivity,
    lifestyle.sleepQuality,
    lifestyle.dietHistory,
    lifestyle.trainingFrequency,
    lifestyle.trainingIntensity,
    lifestyle.trainingModality,
    lifestyle.goal,
  ];
  const smartCount = smartInputs.filter(
    (value) => value !== "" && value !== null && value !== undefined
  ).length;
  score += smartCount * 5;
  if (hasSteps) {
    score += 5;
  }
  if (hasBodyFat) {
    score += 5;
  }

  const exercises = strengthWorkouts.flatMap(
    (workout) => workout.exercises || []
  );
  const cardio = cardioSessions || [];

  const hasStrengthDetail = exercises.some(
    (exercise) => !!exercise.exerciseId
  );

  if (hasStrengthDetail) {
    score += 10;
    const fullRows = exercises.filter(strengthRowComplete);
    if (fullRows.length > 0) {
      score += 10;
    }
    if (fullRows.length >= 3) {
      score += 5;
    }
  }

  if (cardio.length > 0) {
    score += 10;
    const hasIntensity = cardio.some(
      (entry) =>
        parseNumber(entry.duration) > 0 && cardioHasIntensity(entry)
    );
    if (hasIntensity) {
      score += 10;
    }
    const hasHeartRate = cardio.some(
      (entry) => parseNumber(entry.heartRate) > 0
    );
    if (hasHeartRate) {
      score += 5;
    }
  }

  const finalScore = Math.min(95, score);

  const trainingProfileComplete =
    lifestyle.trainingFrequency !== "" &&
    lifestyle.trainingIntensity &&
    lifestyle.trainingModality;
  const lifestyleComplete =
    !!lifestyle.jobActivity &&
    !!lifestyle.sleepQuality &&
    !!lifestyle.dietHistory;
  const cardioIntensityComplete = cardio.some(
    (entry) =>
      parseNumber(entry.duration) > 0 &&
      (cardioHasIntensity(entry) ||
        parseNumber(entry.heartRate) > 0)
  );

  const checklist = [
    {
      label: "Basics complete",
      done: basicsValid,
    },
    {
      label:
        trainingSource === "workout"
          ? "Training profile (from workouts)"
          : "Training profile (freq + intensity + modality)",
      done: trainingProfileComplete,
    },
    {
      label: "Lifestyle context (job + sleep + diet)",
      done: lifestyleComplete,
    },
    {
      label: "Daily steps provided",
      done: hasSteps,
    },
    {
      label: "Body fat % provided",
      done: hasBodyFat,
    },
    {
      label: "Goal selected",
      done: !!lifestyle.goal,
    },
    {
      label: "Weekly strength detail added",
      done: hasStrengthDetail,
    },
    {
      label: "Strength load filled",
      done: exercises.some(strengthRowComplete),
    },
    {
      label: "Weekly cardio detail added",
      done: cardio.length > 0,
    },
    {
      label: "Cardio intensity or heart rate",
      done: cardioIntensityComplete,
    },
  ];

  return { score: finalScore, checklist };
};
